import { find } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslate } from 'core/translate';
import FeatureFilter, {
  FeatureFilterRadio,
  FeatureFilterSection,
} from '../../../../common/featureFilter/FeatureFilter';
import getDriveTypeDescription from '../../../../utils/getDriveTypeDescription';
import { getFirstValidIndex } from '../../../../components/compareManager/utils';
import { getConflictNotificationMessage } from '../../../../components/compareManager/filters/OptionsFilter';
import { withNotifications } from '../../../../common/notifications/NotificationsContext';

const DriveTypesFilter = ({
  driveTypes,
  onChange,
  selectedDriveAndWheel,
  translate,
  analyticsKey,
}) => {
  const showFilter = driveTypes && driveTypes.length > 1;

  return (
    <>
      {showFilter ? (
        <FeatureFilterSection
          onChange={onChange}
          title={translate('drive.type.v3')}
          modifier="drive-type">
          {driveTypes.map(({ driveType, rearWheelType, id, label }) => (
            <FeatureFilterRadio
              key={id}
              name="driveoptions"
              value={id}
              checked={selectedDriveAndWheel?.id === id}
              primaryDescription={label}
              secondaryDescription={
                rearWheelType
                  ? rearWheelType
                  : getDriveTypeDescription(driveType, translate)
              }
              analyticsKey={analyticsKey}
            />
          ))}
        </FeatureFilterSection>
      ) : null}
    </>
  );
};

const PowertrainsFilter = ({
  powertrains,
  onChange,
  selectedPowertrain,
  translate,
  analyticsKey,
}) => {
  const showFilter = powertrains && powertrains.length > 1;

  return (
    <>
      {showFilter ? (
        <FeatureFilterSection
          onChange={onChange}
          title={translate('stp.engine.tab.powertrain')}
          modifier="drive-type">
          {powertrains.map(({ description, id }) => (
            <FeatureFilterRadio
              key={id}
              id={id}
              name="PowertrainOptions"
              value={description}
              checked={selectedPowertrain?.id === id}
              primaryDescription={description}
              analyticsKey={analyticsKey}
            />
          ))}
        </FeatureFilterSection>
      ) : null}
    </>
  );
};

const BodyCompareFilter = ({
  filterCountText,
  availablePowertrains,
  selectedPowertrain,
  availableDriveAndWheels,
  selectedDriveAndWheel,
  onFilterChange,
  isUpdating,
  analyticsKey,
  getFilteredData,
  notifications,
}) => {
  const translate = useTranslate();
  const { show } = notifications;
  const [conflictNotification, setConflictNotification] = useState({
    display: false,
    basedOn: '',
  });
  const conflictNotificationMessage = getConflictNotificationMessage(
    conflictNotification,
    selectedDriveAndWheel,
    selectedPowertrain.description,
    translate('component.standards.group.powertrain'),
    translate
  );

  useEffect(() => {
    if (conflictNotification.display) {
      show(conflictNotificationMessage);
    }
  }, [conflictNotification]);

  const onDriveAndWheelTypeChange = (e) => {
    const selectedOption = find(availableDriveAndWheels, {
      id: e.target.value,
    });

    const proposedOptions = getFilteredData(selectedOption, selectedPowertrain);

    if (proposedOptions.length > 0) {
      setConflictNotification({ display: false, basedOn: '' });
      onFilterChange(selectedOption, selectedPowertrain);
    } else {
      const selectedOptionIndex = availablePowertrains.findIndex(
        (powertrain) => powertrain.id === selectedPowertrain.id
      );

      //Generate an array where each element contains an array of configurations for the selectedOption and an available powertrain.
      //The index of the first non-null element, that has configurations, is the index of the new powertrain.
      const newOptionIndex = getFirstValidIndex(
        availablePowertrains.map((powertrain, index) => {
          return index !== selectedOptionIndex
            ? getFilteredData(selectedOption, powertrain)
            : null;
        })
      );

      const newSelection = availablePowertrains[newOptionIndex];

      setConflictNotification({ display: true, basedOn: 'driveType' });
      onFilterChange(selectedOption, newSelection);
    }
  };

  const onPowertrainChange = (e) => {
    const selectedOption = find(availablePowertrains, { id: e.target.id });

    const proposedOptions = getFilteredData(
      selectedDriveAndWheel,
      selectedOption
    );

    if (proposedOptions.length > 0) {
      setConflictNotification({ display: false, basedOn: '' });
      onFilterChange(selectedDriveAndWheel, selectedOption);
    } else {
      const selectedOptionIndex = availableDriveAndWheels.findIndex(
        (drive) => drive.id === selectedDriveAndWheel.id
      );

      //Generate an array where each element contains an array of configurations for the selectedOption and an available drive type.
      //The index of the first non-null element, that has configurations, is the index of the new drive type.
      const newOptionIndex = getFirstValidIndex(
        availableDriveAndWheels.map((drive, index) => {
          return index !== selectedOptionIndex
            ? getFilteredData(drive, selectedOption)
            : null;
        })
      );

      const newSelection = availableDriveAndWheels[newOptionIndex];

      setConflictNotification({ display: true, basedOn: 'option' });
      onFilterChange(newSelection, selectedOption);
    }
  };

  const calculatePills = () => {
    let pillsList = [];
    if (availableDriveAndWheels.length > 1) {
      pillsList = [
        `${selectedDriveAndWheel.label} ${selectedDriveAndWheel.rearWheelType}`,
      ];
    }
    if (availablePowertrains.length > 1) {
      pillsList = [...pillsList, selectedPowertrain.description];
    }
    return pillsList;
  };

  const pillsList = calculatePills();

  return (
    <>
      <FeatureFilter
        pillsList={pillsList}
        analyticsKey={analyticsKey}
        responsivePills={true}>
        <div className="compare-filter__container">
          <DriveTypesFilter
            driveTypes={availableDriveAndWheels}
            onChange={onDriveAndWheelTypeChange}
            selectedDriveAndWheel={selectedDriveAndWheel}
            translate={translate}
            analyticsKey={`${analyticsKey}:filters:drive type`}
          />
          <PowertrainsFilter
            powertrains={availablePowertrains}
            onChange={onPowertrainChange}
            selectedPowertrain={selectedPowertrain}
            translate={translate}
            analyticsKey={`${analyticsKey}:filters:engine`}
          />
        </div>
      </FeatureFilter>
    </>
  );
};

export default withNotifications(BodyCompareFilter);
